import React, { useEffect, useState } from 'react';
import { Phone, MapPin, Star, Shield } from 'lucide-react';

const Hero = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const stats = [
    { value: '500+', label: 'Vehicles Sold' },
    { value: '98%', label: 'Satisfaction' },
    { value: '10+', label: 'Years Experience' },
  ];

  return (
    <section id="home" className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat hero-bg-ken-burns"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1503376780353-7e6692767b70?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/70 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60" />
        <div className="absolute inset-0 section-grid-bg opacity-40" />
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-[#3DED97]/8 rounded-full blur-[120px] animate-float-slow" />
        <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] bg-[#B0FC38]/6 rounded-full blur-[100px] animate-float-slow-reverse" />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-24 pb-32">
        <div className="max-w-6xl mx-auto text-center">
          <div
            className={`inline-flex items-center gap-2 mb-8 px-5 py-2.5 rounded-full glass-effect transition-all duration-1000 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'
            }`}
          >
            <Star className="h-4 w-4 text-[#3DED97] fill-[#3DED97]" />
            <span className="text-[#3DED97] text-xs sm:text-sm font-bold tracking-[0.2em] uppercase">
              Abuja&apos;s Premier Luxury Dealer
            </span>
            <Shield className="h-4 w-4 text-[#3DED97]" />
          </div>

          <h1
            className={`font-display text-5xl sm:text-6xl lg:text-8xl font-bold mb-8 leading-[1.05] transition-all duration-1000 delay-150 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            <span className="text-white block">Drive Sharp</span>
            <span className="gradient-text animate-gradient-shift block mt-2">Own Excellence</span>
          </h1>

          <p
            className={`text-lg sm:text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-300 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            Experience the finest collection of luxury vehicles with unmatched quality
            and exceptional service — curated for those who demand excellence.
          </p>

          <div
            className={`flex flex-col sm:flex-row gap-4 justify-center items-center mb-14 transition-all duration-1000 delay-450 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <a href="#inventory" className="btn-primary animate-pulse-glow min-w-[200px]">
              View Inventory
            </a>
            <a href="tel:08066312634" className="btn-outline min-w-[200px]">
              <Phone className="h-5 w-5" />
              Call Now
            </a>
          </div>

          <div
            className={`flex flex-col sm:flex-row gap-4 justify-center max-w-xl mx-auto transition-all duration-1000 delay-600 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="flex items-center justify-center gap-3 glass-effect px-6 py-3 rounded-2xl flex-1 hover:border-[#3DED97]/30 transition-colors duration-300">
              <div className="p-2 rounded-full bg-[#3DED97]/10">
                <Phone className="h-4 w-4 text-[#3DED97]" />
              </div>
              <span className="text-white font-medium">08066312634</span>
            </div>
            <div className="flex items-center justify-center gap-3 glass-effect px-6 py-3 rounded-2xl flex-1 hover:border-[#3DED97]/30 transition-colors duration-300">
              <div className="p-2 rounded-full bg-[#3DED97]/10">
                <MapPin className="h-4 w-4 text-[#3DED97]" />
              </div>
              <span className="text-white font-medium">Jesse Jackson Street, Abuja</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div
        className={`relative z-10 border-t border-white/10 bg-black/40 backdrop-blur-md transition-all duration-1000 delay-800 ${
          loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-5xl mx-auto px-4 py-8 grid grid-cols-3 divide-x divide-white/10">
          {stats.map((stat, i) => (
            <div key={i} className="text-center px-4">
              <div className="font-display text-2xl sm:text-3xl font-bold gradient-text mb-1">
                {stat.value}
              </div>
              <div className="text-xs sm:text-sm text-gray-500 uppercase tracking-wider">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className={`absolute bottom-36 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 transition-all duration-1000 delay-1000 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <a href="#inventory" className="group flex flex-col items-center gap-2">
          <span className="text-[10px] tracking-[0.3em] uppercase text-gray-500 group-hover:text-[#3DED97] transition-colors">
            Scroll
          </span>
          <div className="scroll-line" />
        </a>
      </div>
    </section>
  );
};

export default Hero;
